import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getEvents, type RetreatEvent } from '../services/retreat.service';
import { useUrlParam } from '../../../core/hooks/useUrlParam';

interface EventContextType {
    events: RetreatEvent[];
    selectedEventId: number | null;
    selectedEvent: RetreatEvent | null;
    changeEvent: (id: number) => void;
    refreshEvent: () => void;
    isLoading: boolean;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

export const EventProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [events, setEvents] = useState<RetreatEvent[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loadEvents = async () => {
        try {
            const data = await getEvents();
            setEvents(data);

            // Default to active event or first event
            if (data.length > 0) {
                // Try to recover from localStorage (persisted across reloads)
                const stored = localStorage.getItem('vh_retreat_active_event');
                const storedId = stored ? parseInt(stored, 10) : null;
                const restored = storedId ? data.find(e => e.id === storedId) : null;
                const active = restored || data.find(e => e.isActive) || data[0];

                // Only set if not already set, or forced refresh
                setSelectedEventId(prev => prev || active.id);
            }
        } catch (error) {
            console.error('Failed to load events in context:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Load events on mount
    useEffect(() => {
        console.log('EventProvider MOUNTED - Loading Events');
        loadEvents();
    }, []);

    const changeEvent = (id: number) => {
        setSelectedEventId(id);
        localStorage.setItem('vh_retreat_active_event', id.toString());
    };

    // Deep link from the search (`?event=3`): switch to that event
    useUrlParam('event', id => {
        if (events.some(e => e.id === Number(id))) changeEvent(Number(id));
    }, { ready: events.length > 0 });

    const selectedEvent = selectedEventId ? events.find(e => e.id === selectedEventId) || null : null;

    return (
        <EventContext.Provider value={{ events, selectedEventId, selectedEvent, changeEvent, refreshEvent: loadEvents, isLoading }}>
            {children}
        </EventContext.Provider>
    );
};

export const useRetreatEvent = () => {
    const context = useContext(EventContext);
    if (context === undefined) {
        throw new Error('useRetreatEvent must be used within an EventProvider');
    }
    return context;
};
